import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineOutlined from "@mui/icons-material/AddCircleOutlineOutlined";
import DeleteOutlineOutlined from "@mui/icons-material/DeleteOutlineOutlined";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import { GlobalContext } from "../../context/GlobalContext";

const DOCS_URL = "https://app.makeugc.ai/api/platform/documentation";

function ok(res) {
  return res?.data?.status === true;
}

export default function MakeUGC({ lang }) {
  const { hitAxios, showSnack } = React.useContext(GlobalContext);

  const [mainTab, setMainTab] = React.useState(0);
  const [pihSub, setPihSub] = React.useState(0);

  const [avatars, setAvatars] = React.useState([]);
  const [voices, setVoices] = React.useState([]);
  const [loadErr, setLoadErr] = React.useState("");
  const [avatarGender, setAvatarGender] = React.useState("");
  const [voiceLang, setVoiceLang] = React.useState("");
  const [voiceGender, setVoiceGender] = React.useState("");

  const [avatarId, setAvatarId] = React.useState("");
  const [voiceId, setVoiceId] = React.useState("");
  const [voiceScript, setVoiceScript] = React.useState("");
  const [videoName, setVideoName] = React.useState("");
  const [voiceUrl, setVoiceUrl] = React.useState("");
  const [webhookUrl, setWebhookUrl] = React.useState("");
  const [stability, setStability] = React.useState(0.75);
  const [similarityBoost, setSimilarityBoost] = React.useState(0.4);
  const [styleVoice, setStyleVoice] = React.useState(0);
  const [useSpeakerBoost, setUseSpeakerBoost] = React.useState(true);

  const [jobId, setJobId] = React.useState("");
  const [jobStatus, setJobStatus] = React.useState("");
  const [jobUrl, setJobUrl] = React.useState("");
  const [polling, setPolling] = React.useState(false);
  const pollRef = React.useRef(null);

  const [caImage, setCaImage] = React.useState(null);
  const [caVoice, setCaVoice] = React.useState(null);
  const [caBusy, setCaBusy] = React.useState(false);
  const [caResult, setCaResult] = React.useState(null);

  const [novaImageUrl, setNovaImageUrl] = React.useState("");
  const [novaScript, setNovaScript] = React.useState("");
  const [novaVoiceId, setNovaVoiceId] = React.useState("");
  const [novaVoiceUrl, setNovaVoiceUrl] = React.useState("");
  const [novaProjectId, setNovaProjectId] = React.useState("");

  const [omiImageUrl, setOmiImageUrl] = React.useState("");
  const [omiScript, setOmiScript] = React.useState("");
  const [omiVoiceId, setOmiVoiceId] = React.useState("");
  const [omiVoiceUrl, setOmiVoiceUrl] = React.useState("");

  const [veoImageUrl, setVeoImageUrl] = React.useState("");
  const [veoModel, setVeoModel] = React.useState("fast");
  const [veoVoiceId, setVeoVoiceId] = React.useState("");
  const [veoEndImage, setVeoEndImage] = React.useState("");
  const [veoScenes, setVeoScenes] = React.useState([{ script: "", description: "" }]);

  const [pihJobId, setPihJobId] = React.useState("");
  const [pihStatus, setPihStatus] = React.useState("");
  const [pihUrl, setPihUrl] = React.useState("");
  const [pihPolling, setPihPolling] = React.useState(false);
  const pihPollRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (pihPollRef.current) clearInterval(pihPollRef.current);
    };
  }, []);

  const loadAvatars = React.useCallback(async () => {
    const q = avatarGender === "Male" || avatarGender === "Female" ? `?gender=${avatarGender}` : "";
    const res = await hitAxios({
      path: `/api/makeugc/video/avatars${q}`,
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    if (ok(res)) {
      setAvatars(Array.isArray(res.data.data) ? res.data.data : []);
      setLoadErr("");
    } else {
      setAvatars([]);
      setLoadErr(res?.data?.message || res?.data?.msg || "Could not load avatars.");
    }
  }, [hitAxios, avatarGender]);

  const loadVoices = React.useCallback(async () => {
    const p = new URLSearchParams();
    if (voiceLang.trim()) p.set("language", voiceLang.trim());
    if (voiceGender.trim()) p.set("gender", voiceGender.trim());
    const qs = p.toString() ? `?${p}` : "";
    const res = await hitAxios({
      path: `/api/makeugc/video/voices${qs}`,
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    if (ok(res)) {
      setVoices(Array.isArray(res.data.data) ? res.data.data : []);
    } else {
      setVoices([]);
      setLoadErr(res?.data?.message || res?.data?.msg || "Could not load voices.");
    }
  }, [hitAxios, voiceLang, voiceGender]);

  React.useEffect(() => {
    loadAvatars();
  }, [loadAvatars]);

  React.useEffect(() => {
    loadVoices();
  }, [loadVoices]);

  const pollVideo = React.useCallback(
    async (id) => {
      const res = await hitAxios({
        path: `/api/makeugc/video/status?id=${encodeURIComponent(id)}`,
        post: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (!ok(res)) {
        setJobStatus("error");
        return true;
      }
      const d = res.data.data || {};
      const st = String(d.status || "").toLowerCase();
      setJobStatus(st || "…");
      if (d.url) setJobUrl(String(d.url));
      if (st === "completed") {
        showSnack(lang?.makeUgcVideoReady || "Video is ready.", true);
        return true;
      }
      if (st === "failed") {
        showSnack(String(d.reason || d.message || lang?.makeUgcVideoFailed || "Failed."), false);
        return true;
      }
      return false;
    },
    [hitAxios, showSnack, lang],
  );

  const startPollVideo = (id) => {
    setJobId(id);
    setJobUrl("");
    setJobStatus("processing");
    setPolling(true);
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = async () => {
      const done = await pollVideo(id);
      if (done) {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        setPolling(false);
      }
    };
    pollRef.current = setInterval(tick, 6000);
    tick();
  };

  const generateVideo = async () => {
    const script = voiceScript.trim();
    if (!avatarId || !script) {
      showSnack(lang?.makeUgcNeedAvatarScript || "Avatar and script are required.", false);
      return;
    }
    if (script.length > 1500) {
      showSnack(lang?.makeUgcScriptTooLong || "Script must be 1500 characters or less.", false);
      return;
    }
    const body = {
      avatar_id: avatarId,
      voice_script: script,
    };
    if (videoName.trim()) body.video_name = videoName.trim();
    if (voiceUrl.trim()) body.voice_url = voiceUrl.trim();
    if (voiceId.trim()) body.voice_id = voiceId.trim();
    if (webhookUrl.trim()) body.webhook_url = webhookUrl.trim();
    body.voice_settings = {
      stability,
      similarity_boost: similarityBoost,
      style: styleVoice,
      use_speaker_boost: useSpeakerBoost,
    };

    const res = await hitAxios({
      path: "/api/makeugc/video/generate",
      post: true,
      obj: body,
      showLoading: true,
      showSnackbar: false,
    });
    if (ok(res) && res.data.data?.id) {
      showSnack(lang?.makeUgcStarted || "Generation started.", true);
      startPollVideo(res.data.data.id);
    } else {
      showSnack(res?.data?.message || lang?.makeUgcError || "Request failed.", false);
    }
  };

  const submitCustomAvatar = async () => {
    if (!caImage) {
      showSnack(lang?.makeUgcNeedImage || "Choose an image file.", false);
      return;
    }
    const fd = new FormData();
    fd.append("image", caImage);
    if (caVoice) fd.append("voice", caVoice);
    setCaBusy(true);
    setCaResult(null);
    const res = await hitAxios({
      path: "/api/makeugc/custom-avatar/generate",
      post: true,
      obj: fd,
      showLoading: true,
      showSnackbar: false,
    });
    setCaBusy(false);
    if (ok(res)) {
      setCaResult(res.data);
      showSnack(lang?.makeUgcAvatarCreated || "Custom avatar request completed.", true);
    } else {
      showSnack(res?.data?.message || lang?.makeUgcError || "Request failed.", false);
    }
  };

  const pollPih = React.useCallback(
    async (id) => {
      const res = await hitAxios({
        path: `/api/makeugc/product-in-hand/status?id=${encodeURIComponent(id)}`,
        post: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (!ok(res)) {
        setPihStatus("error");
        return true;
      }
      const d = res.data.data || {};
      const st = String(d.status || "").toLowerCase();
      setPihStatus(st || "…");
      if (d.url) setPihUrl(String(d.url));
      if (st === "completed") {
        showSnack(lang?.makeUgcVideoReady || "Video is ready.", true);
        return true;
      }
      if (st === "failed") {
        showSnack(String(d.reason || d.message || lang?.makeUgcVideoFailed || "Failed."), false);
        return true;
      }
      return false;
    },
    [hitAxios, showSnack, lang],
  );

  const startPollPih = (id) => {
    setPihJobId(id);
    setPihUrl("");
    setPihStatus("processing");
    setPihPolling(true);
    if (pihPollRef.current) clearInterval(pihPollRef.current);
    const tick = async () => {
      const done = await pollPih(id);
      if (done) {
        if (pihPollRef.current) clearInterval(pihPollRef.current);
        pihPollRef.current = null;
        setPihPolling(false);
      }
    };
    pihPollRef.current = setInterval(tick, 6000);
    tick();
  };

  const postPih = async (path, body) => {
    const res = await hitAxios({
      path,
      post: true,
      obj: body,
      showLoading: true,
      showSnackbar: false,
    });
    if (ok(res) && res.data.data?.id) {
      showSnack(lang?.makeUgcStarted || "Generation started.", true);
      startPollPih(res.data.data.id);
    } else {
      showSnack(res?.data?.message || lang?.makeUgcError || "Request failed.", false);
    }
  };

  const submitNova = () => {
    if (!novaImageUrl.trim()) {
      showSnack(lang?.makeUgcNeedImageUrl || "Image URL is required.", false);
      return;
    }
    if (!novaVoiceUrl.trim() && (!novaScript.trim() || !novaVoiceId.trim())) {
      showSnack(lang?.makeUgcNovaNeedVoice || "Provide voice_url OR both script and voice_id.", false);
      return;
    }
    const body = { image_url: novaImageUrl.trim() };
    if (novaScript.trim()) body.script = novaScript.trim();
    if (novaVoiceUrl.trim()) body.voice_url = novaVoiceUrl.trim();
    if (novaVoiceId.trim()) body.voice_id = novaVoiceId.trim();
    if (novaProjectId.trim()) body.project_id = novaProjectId.trim();
    postPih("/api/makeugc/product-in-hand/nova-v2/generate", body);
  };

  const submitOmi = () => {
    if (!omiImageUrl.trim()) {
      showSnack(lang?.makeUgcNeedImageUrl || "Image URL is required.", false);
      return;
    }
    if (!omiVoiceUrl.trim() && (!omiScript.trim() || !omiVoiceId.trim())) {
      showSnack(lang?.makeUgcNovaNeedVoice || "Provide voice_url OR both script and voice_id.", false);
      return;
    }
    const body = { image_url: omiImageUrl.trim() };
    if (omiScript.trim()) body.script = omiScript.trim();
    if (omiVoiceUrl.trim()) body.voice_url = omiVoiceUrl.trim();
    if (omiVoiceId.trim()) body.voice_id = omiVoiceId.trim();
    postPih("/api/makeugc/product-in-hand/omi-human/generate", body);
  };

  const submitVeo = () => {
    if (!veoImageUrl.trim()) {
      showSnack(lang?.makeUgcNeedImageUrl || "Image URL is required.", false);
      return;
    }
    const scenes = veoScenes
      .map((s) => ({
        script: s.script.trim(),
        description: s.description.trim(),
      }))
      .filter((s) => s.script || s.description);
    if (!scenes.length) {
      showSnack(lang?.makeUgcVeoScenes || "Add at least one scene with script or description.", false);
      return;
    }
    const body = {
      image_url: veoImageUrl.trim(),
      model: veoModel,
      scenes,
    };
    if (veoVoiceId.trim()) body.voice_id = veoVoiceId.trim();
    if (veoEndImage.trim()) body.end_image_url = veoEndImage.trim();
    postPih("/api/makeugc/product-in-hand/veo/generate", body);
  };

  const paperSx = {
    p: { xs: 2, sm: 2.5 },
    borderRadius: 3,
    border: 1,
    borderColor: "divider",
    bgcolor: "background.paper",
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", pb: 4 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            {lang?.makeUgcTitle || "MakeUGC"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
            {lang?.makeUgcSubtitle ||
              "Generate videos, custom avatars, and product-in-hand clips via the MakeUGC Platform API (proxied with your server key)."}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          endIcon={<OpenInNewOutlined />}
          href={DOCS_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textTransform: "none", fontWeight: 700, flexShrink: 0 }}
        >
          {lang?.makeUgcDocs || "API docs"}
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        {lang?.makeUgcKeyHint ||
          "Set MAKEUGC_API_KEY in your server .env (repo root). The key is never sent to the browser."}
      </Alert>

      {loadErr ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <IconButton
              onClick={() => {
                loadAvatars();
                loadVoices();
              }}
            >
              <RefreshOutlined />
            </IconButton>
          }
        >
          {loadErr}
        </Alert>
      ) : null}

      <Paper elevation={0} sx={{ ...paperSx, mb: 2 }}>
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}
        >
          <Tab label={lang?.makeUgcTabVideo || "Video"} />
          <Tab label={lang?.makeUgcTabAvatar || "Custom avatar"} />
          <Tab label={lang?.makeUgcTabPih || "Product in hand"} />
        </Tabs>

        {mainTab === 0 && (
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={5}>
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                {lang?.makeUgcAvatars || "Avatars"}
              </Typography>
              <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                <InputLabel>{lang?.makeUgcGenderFilter || "Gender filter"}</InputLabel>
                <Select
                  label={lang?.makeUgcGenderFilter || "Gender filter"}
                  value={avatarGender}
                  onChange={(e) => setAvatarGender(e.target.value)}
                >
                  <MenuItem value="">{lang?.makeUgcAll || "All"}</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ maxHeight: 360, overflowY: "auto", pr: 0.5 }}>
                <Grid container spacing={1}>
                  {avatars.map((a) => (
                    <Grid item xs={4} key={a.id}>
                      <Box
                        onClick={() => setAvatarId(a.id)}
                        sx={{
                          borderRadius: 2,
                          overflow: "hidden",
                          cursor: "pointer",
                          border: "2px solid",
                          borderColor: avatarId === a.id ? "primary.main" : "divider",
                          "&:hover": { borderColor: "primary.light" },
                        }}
                      >
                        <Box sx={{ pt: "120%", position: "relative", bgcolor: "grey.200" }}>
                          {a.thumbnail ? (
                            <Box
                              component="img"
                              src={a.thumbnail}
                              alt=""
                              sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : null}
                        </Box>
                        <Typography variant="caption" display="block" noWrap sx={{ px: 0.5, py: 0.5 }}>
                          {a.name}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                {lang?.makeUgcVoices || "Voices"}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1.5 }}>
                <TextField
                  size="small"
                  label={lang?.makeUgcVoiceLang || "Language"}
                  value={voiceLang}
                  onChange={(e) => setVoiceLang(e.target.value)}
                  fullWidth
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>{lang?.makeUgcGenderFilter || "Gender"}</InputLabel>
                  <Select
                    label={lang?.makeUgcGenderFilter || "Gender"}
                    value={voiceGender}
                    onChange={(e) => setVoiceGender(e.target.value)}
                  >
                    <MenuItem value="">{lang?.makeUgcAll || "All"}</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>{lang?.makeUgcPickVoice || "Voice (optional override)"}</InputLabel>
                <Select
                  label={lang?.makeUgcPickVoice || "Voice (optional override)"}
                  value={voiceId}
                  onChange={(e) => setVoiceId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>{lang?.makeUgcAvatarDefaultVoice || "Avatar default"}</em>
                  </MenuItem>
                  {voices.map((v) => (
                    <MenuItem key={v.id || v.voiceId} value={v.voiceId || v.id}>
                      {v.name} {v.language ? `· ${v.language}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                minRows={5}
                label={lang?.makeUgcVoiceScript || "Voice script"}
                placeholder={lang?.makeUgcVoiceScriptPh || "Up to 1500 characters…"}
                value={voiceScript}
                onChange={(e) => setVoiceScript(e.target.value)}
                helperText={`${voiceScript.length}/1500`}
                error={voiceScript.length > 1500}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                size="small"
                label={lang?.makeUgcVideoName || "Video name (optional)"}
                value={videoName}
                onChange={(e) => setVideoName(e.target.value)}
                sx={{ mb: 1 }}
              />
              <TextField
                fullWidth
                size="small"
                label={lang?.makeUgcVoiceUrl || "voice_url (optional)"}
                value={voiceUrl}
                onChange={(e) => setVoiceUrl(e.target.value)}
                sx={{ mb: 1 }}
              />
              <TextField
                fullWidth
                size="small"
                label={lang?.makeUgcWebhook || "webhook_url (optional)"}
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                {lang?.makeUgcVoiceSettings || "voice_settings"}
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label="stability"
                    inputProps={{ min: 0, max: 1, step: 0.05 }}
                    value={stability}
                    onChange={(e) => setStability(Number(e.target.value))}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label="similarity_boost"
                    inputProps={{ min: 0, max: 1, step: 0.05 }}
                    value={similarityBoost}
                    onChange={(e) => setSimilarityBoost(Number(e.target.value))}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label="style"
                    inputProps={{ min: 0, max: 1, step: 0.05 }}
                    value={styleVoice}
                    onChange={(e) => setStyleVoice(Number(e.target.value))}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>use_speaker_boost</InputLabel>
                    <Select
                      label="use_speaker_boost"
                      value={useSpeakerBoost ? "yes" : "no"}
                      onChange={(e) => setUseSpeakerBoost(e.target.value === "yes")}
                    >
                      <MenuItem value="yes">true</MenuItem>
                      <MenuItem value="no">false</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Button
                variant="contained"
                size="large"
                onClick={generateVideo}
                disabled={polling}
                sx={{ fontWeight: 800, textTransform: "none", borderRadius: 2 }}
              >
                {polling ? <CircularProgress size={22} color="inherit" /> : lang?.makeUgcGenerate || "Generate video"}
              </Button>

              {(jobId || jobStatus) && (
                <Alert severity={jobUrl ? "success" : "info"} sx={{ mt: 2, borderRadius: 2 }}>
                  <Typography variant="body2">
                    {(lang?.makeUgcJobStatus || "Status: {s}").replace("{s}", jobStatus || "…")}
                    {jobId ? ` · id: ${jobId}` : ""}
                  </Typography>
                  {jobUrl ? (
                    <Link href={jobUrl} target="_blank" rel="noopener noreferrer" fontWeight={700}>
                      {lang?.makeUgcOpenVideo || "Open video"}
                    </Link>
                  ) : null}
                </Alert>
              )}
            </Grid>
          </Grid>
        )}

        {mainTab === 1 && (
          <Stack spacing={2} sx={{ maxWidth: 560 }}>
            <Typography variant="body2" color="text.secondary">
              {lang?.makeUgcCaHelp || "POST /custom-avatar/generate — image required (max 10MB), voice optional."}
            </Typography>
            <Button variant="outlined" component="label" sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 700 }}>
              {lang?.makeUgcPickImage || "Choose image"}
              <input type="file" hidden accept="image/*" onChange={(e) => setCaImage(e.target.files?.[0] || null)} />
            </Button>
            {caImage ? <Chip label={caImage.name} onDelete={() => setCaImage(null)} /> : null}
            <Button variant="outlined" component="label" sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 700 }}>
              {lang?.makeUgcPickVoiceFile || "Optional voice sample"}
              <input type="file" hidden accept="audio/*" onChange={(e) => setCaVoice(e.target.files?.[0] || null)} />
            </Button>
            {caVoice ? <Chip label={caVoice.name} onDelete={() => setCaVoice(null)} /> : null}
            <Button variant="contained" disabled={caBusy} onClick={submitCustomAvatar} sx={{ fontWeight: 800, textTransform: "none" }}>
              {caBusy ? <CircularProgress size={22} color="inherit" /> : lang?.makeUgcSubmitAvatar || "Create custom avatar"}
            </Button>
            {caResult ? (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                <Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
                  {JSON.stringify(caResult, null, 2)}
                </Typography>
              </Alert>
            ) : null}
          </Stack>
        )}

        {mainTab === 2 && (
          <Box>
            <Tabs value={pihSub} onChange={(_, v) => setPihSub(v)} sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}>
              <Tab label="Nova V2" />
              <Tab label="Omi-Human" />
              <Tab label="Veo" />
            </Tabs>

            {pihSub === 0 && (
              <Stack spacing={1.5} sx={{ maxWidth: 640 }}>
                <TextField required label="image_url" fullWidth value={novaImageUrl} onChange={(e) => setNovaImageUrl(e.target.value)} />
                <TextField label="script" fullWidth multiline minRows={3} value={novaScript} onChange={(e) => setNovaScript(e.target.value)} />
                <TextField label="voice_id" fullWidth value={novaVoiceId} onChange={(e) => setNovaVoiceId(e.target.value)} />
                <TextField label="voice_url" fullWidth value={novaVoiceUrl} onChange={(e) => setNovaVoiceUrl(e.target.value)} />
                <TextField label="project_id" fullWidth value={novaProjectId} onChange={(e) => setNovaProjectId(e.target.value)} />
                <Typography variant="caption" color="text.secondary">
                  {lang?.makeUgcNovaLimits || "Nova V2: audio up to ~90s. Credits: 1 per generation (per MakeUGC docs)."}
                </Typography>
                <Button variant="contained" onClick={submitNova} disabled={pihPolling} sx={{ fontWeight: 800, textTransform: "none" }}>
                  {lang?.makeUgcGenerate || "Generate"}
                </Button>
              </Stack>
            )}

            {pihSub === 1 && (
              <Stack spacing={1.5} sx={{ maxWidth: 640 }}>
                <TextField required label="image_url" fullWidth value={omiImageUrl} onChange={(e) => setOmiImageUrl(e.target.value)} />
                <TextField label="script" fullWidth multiline minRows={3} value={omiScript} onChange={(e) => setOmiScript(e.target.value)} />
                <TextField label="voice_id" fullWidth value={omiVoiceId} onChange={(e) => setOmiVoiceId(e.target.value)} />
                <TextField label="voice_url" fullWidth value={omiVoiceUrl} onChange={(e) => setOmiVoiceUrl(e.target.value)} />
                <Typography variant="caption" color="text.secondary">
                  {lang?.makeUgcOmiLimits || "Omi-Human: audio up to ~30s. Dynamic credits per MakeUGC."}
                </Typography>
                <Button variant="contained" onClick={submitOmi} disabled={pihPolling} sx={{ fontWeight: 800, textTransform: "none" }}>
                  {lang?.makeUgcGenerate || "Generate"}
                </Button>
              </Stack>
            )}

            {pihSub === 2 && (
              <Stack spacing={1.5}>
                <TextField required label="image_url" fullWidth value={veoImageUrl} onChange={(e) => setVeoImageUrl(e.target.value)} />
                <FormControl size="small" sx={{ maxWidth: 200 }}>
                  <InputLabel>model</InputLabel>
                  <Select label="model" value={veoModel} onChange={(e) => setVeoModel(e.target.value)}>
                    <MenuItem value="fast">fast</MenuItem>
                    <MenuItem value="pro">pro</MenuItem>
                  </Select>
                </FormControl>
                <TextField label="voice_id" fullWidth value={veoVoiceId} onChange={(e) => setVeoVoiceId(e.target.value)} />
                <TextField label="end_image_url" fullWidth value={veoEndImage} onChange={(e) => setVeoEndImage(e.target.value)} />
                <Typography fontWeight={700}>{lang?.makeUgcVeoScenesTitle || "Scenes (1–4)"}</Typography>
                {veoScenes.map((s, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack spacing={1}>
                      <Typography variant="caption" color="text.secondary">
                        #{i + 1}
                      </Typography>
                      <TextField
                        size="small"
                        label="script"
                        fullWidth
                        value={s.script}
                        onChange={(e) => {
                          const next = [...veoScenes];
                          next[i] = { ...next[i], script: e.target.value };
                          setVeoScenes(next);
                        }}
                      />
                      <TextField
                        size="small"
                        label="description"
                        fullWidth
                        value={s.description}
                        onChange={(e) => {
                          const next = [...veoScenes];
                          next[i] = { ...next[i], description: e.target.value };
                          setVeoScenes(next);
                        }}
                      />
                    </Stack>
                  </Paper>
                ))}
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={<AddCircleOutlineOutlined />}
                    disabled={veoScenes.length >= 4}
                    onClick={() => setVeoScenes([...veoScenes, { script: "", description: "" }])}
                    sx={{ textTransform: "none" }}
                  >
                    {lang?.makeUgcAddScene || "Add scene"}
                  </Button>
                  {veoScenes.length > 1 ? (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlineOutlined />}
                      onClick={() => setVeoScenes(veoScenes.slice(0, -1))}
                      sx={{ textTransform: "none" }}
                    >
                      {lang?.makeUgcRemoveScene || "Remove last"}
                    </Button>
                  ) : null}
                </Stack>
                <Button variant="contained" onClick={submitVeo} disabled={pihPolling} sx={{ fontWeight: 800, textTransform: "none", maxWidth: 240 }}>
                  {lang?.makeUgcGenerate || "Generate"}
                </Button>
              </Stack>
            )}

            {(pihJobId || pihStatus) && (
              <Alert severity={pihUrl ? "success" : "info"} sx={{ mt: 3, borderRadius: 2 }}>
                <Typography variant="body2">
                  {(lang?.makeUgcJobStatus || "Status: {s}").replace("{s}", pihStatus || "…")}
                  {pihJobId ? ` · id: ${pihJobId}` : ""}
                </Typography>
                {pihUrl ? (
                  <Link href={pihUrl} target="_blank" rel="noopener noreferrer" fontWeight={700}>
                    {lang?.makeUgcOpenVideo || "Open video"}
                  </Link>
                ) : null}
              </Alert>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
